import { useState, type FormEvent } from "react";
import { useParams } from "react-router";
import type { Address, PublicShareForm, PublicSubmitBody } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { Logo } from "@/shared/components/ui/Logo";
import { FormField } from "@/shared/components/form/FormField";
import { TextareaFormField } from "@/shared/components/form/TextareaFormField";
import { AddressFields } from "@/shared/components/form/AddressFields";
import {
  usePublicShareForm,
  useSubmitPublicShare,
  useVerifyAccessCode,
} from "@/shared/lib/publicShare";

const EMPTY_ADDRESS: Address = {
  line1: null,
  line2: null,
  city: null,
  postalCode: null,
  countryCode: null,
  countryName: null,
  stateCode: null,
  stateName: null,
};

interface FormState {
  name: string;
  email: string;
  phone: string;
  notes: string;
  address: Address;
}

/**
 * Public landing page (/share/:token). No auth — the token is the credential.
 * Recipients enter their own contact details, which become a pending
 * submission for the owner to review (never a direct contact write). When the
 * link is gated, an access code must be entered before the form is shown.
 */
export function CollectShare() {
  const { token } = useParams<{ token: string }>();
  const form = usePublicShareForm(token);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="px-6 py-6 lg:px-10">
        <Logo to="/" />
      </header>
      <main className="flex-1">
        <Section spacing="md">
          <Container size="2xl">
            {form.isLoading ? (
              <p className="type-body-base text-text-muted">Loading…</p>
            ) : form.isError ? (
              <EmptyState
                variant="page"
                title="This link isn't active"
                description="The link you followed has expired or been turned off. Ask whoever shared it with you for a new one."
              />
            ) : form.data?.status === "disabled" ? (
              <EmptyState
                variant="page"
                title="Sharing is turned off"
                description="The owner has turned off this page for now. Check back later, or ask whoever shared it with you to turn it back on."
              />
            ) : form.data ? (
              <ShareFlow token={token} data={form.data} />
            ) : null}
          </Container>
        </Section>
      </main>
    </div>
  );
}

function ShareFlow({
  token,
  data,
}: {
  token: string | undefined;
  data: PublicShareForm;
}) {
  // Holds the code once verified, so it can ride along on the submission.
  const [verifiedCode, setVerifiedCode] = useState<string | null>(null);

  if (data.requiresAccessCode && verifiedCode === null) {
    return (
      <AccessCodeGate
        token={token}
        requesterName={data.requesterName}
        onVerified={setVerifiedCode}
      />
    );
  }

  return (
    <ShareForm
      token={token}
      requesterName={data.requesterName}
      accessCode={verifiedCode ?? undefined}
    />
  );
}

function AccessCodeGate({
  token,
  requesterName,
  onVerified,
}: {
  token: string | undefined;
  requesterName: string | null;
  onVerified: (code: string) => void;
}) {
  const verify = useVerifyAccessCode(token);
  const [code, setCode] = useState("");

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = code.trim();
    verify.mutate(value, { onSuccess: () => onVerified(value) });
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="type-heading-lg text-text">Enter access code</h1>
        <p className="type-body-sm text-text-muted">
          {requesterName
            ? `${requesterName} protected this page with a code. Enter it to continue.`
            : "This page is protected by a code. Enter it to continue."}
        </p>
      </div>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <FormField
          label="Access code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={verify.isError ? "Incorrect access code" : undefined}
          autoFocus
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={verify.isPending || !code.trim()}>
            {verify.isPending ? "Checking…" : "Continue"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ShareForm({
  token,
  requesterName,
  accessCode,
}: {
  token: string | undefined;
  requesterName: string | null;
  accessCode: string | undefined;
}) {
  const submit = useSubmitPublicShare(token);
  const [submitted, setSubmitted] = useState(false);
  const [state, setState] = useState<FormState>({
    name: "",
    email: "",
    phone: "",
    notes: "",
    address: { ...EMPTY_ADDRESS },
  });

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: PublicSubmitBody = {
      name: state.name.trim() || undefined,
      email: state.email.trim() || undefined,
      phone: state.phone.trim() || undefined,
      notes: state.notes.trim() || undefined,
      address: state.address,
      ...(accessCode ? { accessCode } : {}),
    };
    submit.mutate(payload, { onSuccess: () => setSubmitted(true) });
  }

  if (submitted) {
    return (
      <EmptyState
        variant="page"
        title="Thank you!"
        description="Your details have been sent. You can close this page now."
      />
    );
  }

  const heading = requesterName
    ? `${requesterName} would like your details`
    : "Share your details";

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="type-heading-lg text-text">{heading}</h1>
        <p className="type-body-sm text-text-muted">
          Fill in what you're comfortable sharing.
        </p>
      </div>

      <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
        <section className="flex flex-col gap-6">
          <FormField
            label="Name"
            required
            autoComplete="name"
            value={state.name}
            onChange={(e) => patch("name", e.target.value)}
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField
              label="Email"
              type="email"
              autoComplete="email"
              value={state.email}
              onChange={(e) => patch("email", e.target.value)}
            />
            <FormField
              label="Phone"
              type="tel"
              autoComplete="tel"
              value={state.phone}
              onChange={(e) => patch("phone", e.target.value)}
            />
          </div>
        </section>

        <AddressFields value={state.address} onChange={(next) => patch("address", next)} />

        <section className="flex flex-col gap-6">
          <TextareaFormField
            label="Anything else?"
            rows={3}
            value={state.notes}
            onChange={(e) => patch("notes", e.target.value)}
          />
        </section>

        {submit.isError ? (
          <p className="type-body-sm text-danger-strong mx-4" role="alert">
            {submit.error.message}
          </p>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" disabled={submit.isPending || !state.name.trim()}>
            {submit.isPending ? "Sending…" : "Send my details"}
          </Button>
        </div>
      </form>
    </div>
  );
}
